// Swapping <main> in place: the tv channel change and client-side navigation both do it,
// so the header (and the audio context living under it) carries across the whole site.
import { reduced } from "./dom.ts";
import { T } from "./timing.ts";
import { syncPanel } from "./panelui.ts";

/**
 * Fetch a page and swap its <main> into this one, with a fade. Resolves to the fetched
 * document (for its title, its header bits); rejects if the fetch fails or either page has
 * no <main>. `onBlank` runs after the fade-out, before the new content lands.
 */
export async function swapMain(
  href: string,
  opts: { headers?: Record<string, string>; onBlank?: () => void } = {},
): Promise<{ doc: Document; url: string }> {
  const res = await fetch(href, { cache: "no-store", credentials: "same-origin", headers: opts.headers });
  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, "text/html");
  const fresh = doc.querySelector("main"),
    cur = document.querySelector("main");
  if (!fresh || !cur) throw new Error("no <main> to swap");
  cur.classList.add("switching");
  await new Promise((r) => setTimeout(r, reduced ? 0 : T.SWITCH_FADE));
  if (opts.onBlank) opts.onBlank();
  cur.innerHTML = fresh.innerHTML;
  cur.classList.remove("switching");
  window.scrollTo({ top: 0, behavior: "auto" });
  return { doc, url: res.url || href };
}

/** a stake being typed on the panel: the form (by its action), the fields, and which one had the focus */
type Stake = { action: string; side: string; amount: string; focus: string | null; open: boolean };

/**
 * Swap the side panel in from a fetched home page (the tv changing channel, #557): every section fresh from the server,
 * each list's scroll where it was, and a stake being typed (the form under the focus, or one changed from its defaults)
 * carried over. Nothing happens without a panel on both pages.
 */
export function swapPanel(doc: Document): boolean {
  const fresh = doc.querySelector("aside.side-panel"),
    cur = document.querySelector("aside.side-panel");
  if (!fresh || !cur) return false;
  const scrolls = new Map<string, number>();
  for (const sec of cur.querySelectorAll("section")) {
    const l = sec.querySelector(":scope > ol, :scope > ul");
    if (l && l.scrollTop) scrolls.set(sec.className, l.scrollTop);
  }
  const typed: Stake[] = [];
  for (const f of cur.querySelectorAll("form.stake")) {
    const side = f.querySelector<HTMLSelectElement>("select[name=side]"),
      amount = f.querySelector<HTMLInputElement>("input[name=amount]");
    if (!side || !amount) continue;
    const focus = f.contains(document.activeElement)
      ? (document.activeElement as HTMLElement).getAttribute("name")
      : null;
    const open = !!f.closest("li.open");
    if (open || focus || side.value !== "east" || amount.value !== amount.defaultValue)
      typed.push({ action: f.getAttribute("action") || "", side: side.value, amount: amount.value, focus, open });
  }
  cur.replaceChildren(...Array.from(fresh.childNodes).map((n) => document.importNode(n, true)));
  for (const sec of cur.querySelectorAll("section")) {
    const top = scrolls.get(sec.className),
      l = sec.querySelector(":scope > ol, :scope > ul");
    if (top && l) l.scrollTop = top;
  }
  for (const t of typed) {
    const f = Array.from(cur.querySelectorAll("form.stake")).find((f) => (f.getAttribute("action") || "") === t.action);
    if (!f) continue; // the pairing left the queue: the stake goes with it
    const side = f.querySelector<HTMLSelectElement>("select[name=side]"),
      amount = f.querySelector<HTMLInputElement>("input[name=amount]");
    if (side) side.value = t.side;
    if (amount) amount.value = t.amount;
    if (t.open) f.closest("li")?.classList.add("open"); // the row the viewer opened stays open
    if (t.focus) {
      const el = f.querySelector<HTMLElement>(`[name="${t.focus}"]`);
      if (el) {
        el.focus({ preventScroll: true });
        const inp = el as Partial<HTMLInputElement>;
        if (typeof inp.setSelectionRange === "function") {
          try {
            inp.setSelectionRange(inp.value?.length ?? 0, inp.value?.length ?? 0);
          } catch (_) {
            /* a number input has no selection */
          }
        }
      }
    }
  }
  syncPanel(cur); // the folds, sizes and sheet are the html element's and stand; the fresh headings, tabs and rows are told
  return true;
}

/**
 * Swap the next bout into the ring in place, continuously: no fade, the stage stays. Fetches the page, and puts the
 * fresh bout's markup and data attributes into the current `article.bout`, keeping the stage's canvases where they are — so the arena and the wreck carry across, and the clouds see a new bout on the
 * same stage and walk the next pair on. Rejects if the fetch fails or either page has no bout.
 */
export async function swapBout(
  href: string,
  opts: { headers?: Record<string, string>; onBlank?: () => void } = {},
): Promise<{ doc: Document; url: string }> {
  const res = await fetch(href, { cache: "no-store", credentials: "same-origin", headers: opts.headers });
  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, "text/html");
  const fresh = doc.querySelector("article.bout") as HTMLElement | null,
    cur = document.querySelector("article.bout") as HTMLElement | null;
  if (!fresh || !cur) throw new Error("no bout to swap");
  if (opts.onBlank) opts.onBlank();
  // the data attributes: the fresh bout's, wholesale
  for (const k of Object.keys(cur.dataset)) delete cur.dataset[k];
  for (const [k, v] of Object.entries(fresh.dataset)) if (v !== undefined) cur.dataset[k] = v;
  // the children: everything but the stage's own canvases (the front, the gl layer) goes; the fresh bout's come in
  const keep = new Set(Array.from(cur.children).filter((el) => el.matches("canvas")));
  for (const el of Array.from(cur.children)) if (!keep.has(el)) el.remove();
  for (const el of Array.from(fresh.children)) {
    if (el.matches("canvas")) continue;
    cur.appendChild(document.importNode(el, true));
  }
  if (doc.title) document.title = doc.title;
  return { doc, url: res.url || href };
}
